import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Plus, History, ArrowUpRight, ArrowDownLeft, Loader2, Users } from 'lucide-react';
import { homeAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      const response = await homeAPI.get();
      setHomeData(response.data);
    } catch (err) {
      setError('Couldn\'t load your data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = Math.abs(amount);
    if (num === Math.floor(num)) {
      return `₹${num.toLocaleString('en-IN')}`;
    }
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <p className="text-lg font-medium mb-4">{error}</p>
        <button onClick={fetchHomeData} className="btn-secondary">
          Try Again
        </button>
      </div>
    );
  }

  const { balance, recent_activity } = homeData || {};
  const displayName = user?.name || 'there';

  return (
    <div className="min-h-screen bg-white pb-32">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center justify-between border-b-2 border-black">
        <h1 className="text-2xl font-heading font-bold">
          Hi, {displayName.split(' ')[0]}
        </h1>
        <button
          onClick={() => navigate('/profile')}
          className="w-12 h-12 rounded-full border-2 border-black flex items-center justify-center hover:bg-neutral-100 transition-colors"
          data-testid="profile-button"
        >
          <User className="w-6 h-6" />
        </button>
      </header>

      <main className="p-6 space-y-6">
        {/* Balance Card */}
        <div className="card-base" data-testid="balance-card">
          {balance?.net_balance >= 0 ? (
            <>
              <p className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-1">
                You are owed
              </p>
              <p className="text-4xl font-heading font-bold text-money-in">
                {formatCurrency(balance?.total_owed_to_you || 0)}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-1">
                You owe
              </p>
              <p className="text-4xl font-heading font-bold text-money-out">
                {formatCurrency(balance?.total_you_owe || 0)}
              </p>
            </>
          )}
          
          {balance?.net_balance === 0 && balance?.total_owed_to_you === 0 && (
            <p className="text-4xl font-heading font-bold text-neutral-400">
              You're all settled!
            </p>
          )}

          {/* Breakdown */}
          {(balance?.total_owed_to_you > 0 || balance?.total_you_owe > 0) && (
            <div className="mt-4 pt-4 border-t-2 border-dashed border-neutral-200 flex gap-6">
              <div>
                <p className="text-xs text-neutral-500 uppercase">Owed to you</p>
                <p className="font-bold text-money-in">{formatCurrency(balance?.total_owed_to_you || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase">You owe</p>
                <p className="font-bold text-money-out">{formatCurrency(balance?.total_you_owe || 0)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5" />
            <h2 className="text-lg font-heading font-bold">Recent Activity</h2>
          </div>

          {recent_activity?.length === 0 ? (
            <div className="card-base text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-100 flex items-center justify-center">
                <Plus className="w-8 h-8 text-neutral-400" />
              </div>
              <p className="text-lg font-medium mb-1">No splits yet</p>
              <p className="text-neutral-500 text-sm">
                Create your first split and settle up instantly
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recent_activity?.map((item) => (
                <button
                  key={item.expense_id}
                  onClick={() => navigate(`/expenses/${item.expense_id}`)}
                  className="w-full card-base hover:-translate-y-1 transition-transform text-left flex items-center gap-4"
                  data-testid={`activity-item-${item.expense_id}`}
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    item.is_owed_to_you ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {item.is_owed_to_you ? (
                      <ArrowDownLeft className="w-6 h-6 text-money-in" />
                    ) : (
                      <ArrowUpRight className="w-6 h-6 text-money-out" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">
                      {item.friend_name || item.friend_phone || 'Friend'}
                    </p>
                    <p className="text-sm text-neutral-500 truncate">
                      {item.description || 'Split'}
                    </p>
                  </div>

                  {/* Amount & Status */}
                  <div className="text-right">
                    <p className={`font-bold ${
                      item.is_owed_to_you ? 'text-money-in' : 'text-money-out'
                    }`}>
                      {item.is_owed_to_you ? '+' : '-'}{formatCurrency(item.amount)}
                    </p>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      item.status === 'settled' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {item.status === 'settled' ? 'Settled' : 'Pending'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black">
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/groups')}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
            data-testid="groups-button"
          >
            <Users className="w-5 h-5" />
            Groups
          </button>
          <button
            onClick={() => navigate('/split/new')}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
            data-testid="quick-split-button"
          >
            <Plus className="w-5 h-5" />
            Quick Split
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
